/**
 * Filtrage et tri de collection, côté client.
 *
 * Pourquoi pas les facettes natives Shopify : matière et couleur sont des
 * chaînes à virgules (« Canvas, Leather »). Shopify les traiterait comme une
 * valeur unique — 25 facettes matière au lieu de 10. On découpe donc les
 * listes et on filtre ici.
 *
 * Règles : OR à l'intérieur d'une facette, AND entre facettes.
 * L'état est reflété dans l'URL pour que tout tri/filtre soit partageable.
 */
class VshCollection extends HTMLElement {
  connectedCallback() {
    this.cards = Array.from(this.querySelectorAll('[data-vsh-card]'));
    this.results = this.querySelector('[data-vsh-results]');
    this.empty = this.querySelector('[data-vsh-empty]');
    this.countEl = this.querySelector('[data-vsh-count]');
    this.chipsEl = this.querySelector('[data-vsh-chips]');
    this.panel = this.querySelector('[data-vsh-facets], #vsh-facets');
    this.activeBadge = this.querySelector('[data-vsh-active-count]');
    this.sortEl = this.querySelector('[data-vsh-sort]');
    this.boxes = Array.from(this.querySelectorAll('[data-vsh-facet]'));
    this.priceInputs = Array.from(this.querySelectorAll('[data-vsh-price]'));

    // Mémorise l'ordre d'origine pour pouvoir revenir au tri « manuel ».
    this.cards.forEach((c, i) => (c.dataset.vshIndex = String(i)));

    this.labels = new Map();
    this.boxes.forEach((b) => {
      const span = b.parentElement.querySelector('span');
      this.labels.set(b, span ? span.textContent.trim() : b.value);
    });

    this.bind();
    this.readUrl();
    this.apply({ push: false });
  }

  bind() {
    this.querySelectorAll('[data-vsh-filter-toggle]').forEach((btn) =>
      btn.addEventListener('click', () => this.togglePanel())
    );

    this.boxes.forEach((b) => b.addEventListener('change', () => this.apply()));

    this.priceInputs.forEach((i) => {
      let t;
      i.addEventListener('input', () => {
        clearTimeout(t);
        t = setTimeout(() => this.apply(), 250);
      });
    });

    if (this.sortEl) this.sortEl.addEventListener('change', () => this.apply());

    this.querySelectorAll('[data-vsh-clear]').forEach((btn) =>
      btn.addEventListener('click', () => this.clear())
    );

    window.addEventListener('popstate', () => {
      this.readUrl();
      this.apply({ push: false });
    });
  }

  togglePanel() {
    const open = this.panel.dataset.open === 'true';
    this.panel.dataset.open = open ? 'false' : 'true';
    this.querySelectorAll('[data-vsh-filter-toggle]').forEach((b) =>
      b.setAttribute('aria-expanded', String(!open))
    );
  }

  /** Facettes cochées, regroupées par clé. */
  selection() {
    const sel = {};
    this.boxes.forEach((b) => {
      if (!b.checked) return;
      (sel[b.dataset.vshFacet] ||= new Set()).add(b.value.toLowerCase());
    });
    return sel;
  }

  price() {
    const get = (k) => {
      const el = this.priceInputs.find((i) => i.dataset.vshPrice === k);
      const v = el && el.value !== '' ? parseFloat(el.value) : null;
      return Number.isFinite(v) ? v : null;
    };
    return { min: get('min'), max: get('max') };
  }

  /** Un critère de facette est-il satisfait par la carte ? */
  static hit(card, key, wanted) {
    const raw = (card.dataset[key] || '').toLowerCase();
    if (!raw) return false;
    // material / color sont des listes séparées par des barres verticales
    const tokens = raw.includes('|') ? raw.split('|').map((s) => s.trim()) : [raw];
    for (const w of wanted) if (tokens.includes(w)) return true;
    return false;
  }

  matches(card, sel, price, skip) {
    for (const [key, wanted] of Object.entries(sel)) {
      if (key === skip) continue;
      if (!VshCollection.hit(card, key, wanted)) return false;
    }
    const p = parseFloat(card.dataset.price);
    if (price.min !== null && p < price.min) return false;
    if (price.max !== null && p > price.max) return false;
    return true;
  }

  apply({ push = true } = {}) {
    const sel = this.selection();
    const price = this.price();

    let shown = 0;
    this.cards.forEach((card) => {
      const ok = this.matches(card, sel, price);
      card.hidden = !ok;
      if (ok) shown++;
    });

    this.updateCounts(sel, price);
    this.sort();
    this.chips(sel, price);

    if (this.countEl) this.countEl.textContent = String(shown);
    if (this.empty) this.empty.hidden = shown !== 0;
    if (this.results) this.results.hidden = shown === 0;

    const n = Object.values(sel).reduce((a, s) => a + s.size, 0) +
      (price.min !== null ? 1 : 0) + (price.max !== null ? 1 : 0);
    if (this.activeBadge) {
      this.activeBadge.textContent = String(n);
      this.activeBadge.hidden = n === 0;
    }

    if (push) this.writeUrl(sel, price);
  }

  /**
   * Compte, pour chaque option, ce qu'elle donnerait si on la cochait —
   * en ignorant sa propre facette (sinon toute option non cochée tombe à 0).
   */
  updateCounts(sel, price) {
    const byFacet = {};
    this.boxes.forEach((b) => (byFacet[b.dataset.vshFacet] ||= []).push(b));

    Object.entries(byFacet).forEach(([key, boxes]) => {
      const pool = this.cards.filter((c) => this.matches(c, sel, price, key));
      boxes.forEach((b) => {
        const n = pool.filter((c) => VshCollection.hit(c, key, [b.value.toLowerCase()])).length;
        const out = b.parentElement.querySelector('.vsh-check__count');
        if (out) out.textContent = n ? String(n) : '';
        b.parentElement.classList.toggle('vsh-check--empty', n === 0 && !b.checked);
      });
    });
  }

  sort() {
    if (!this.results || !this.sortEl) return;
    const mode = this.sortEl.value;
    const visible = this.cards.filter((c) => !c.hidden);

    const cmp = {
      'price-asc': (a, b) => a.dataset.price - b.dataset.price,
      'price-desc': (a, b) => b.dataset.price - a.dataset.price,
      'title-asc': (a, b) =>
        (a.dataset.house || '').localeCompare(b.dataset.house || '', 'fr') ||
        (a.dataset.title || '').localeCompare(b.dataset.title || '', 'fr'),
      manual: (a, b) => a.dataset.vshIndex - b.dataset.vshIndex,
    }[mode];

    if (!cmp) return;
    visible.sort(cmp).forEach((c) => this.results.appendChild(c));
  }

  chips(sel, price) {
    if (!this.chipsEl) return;
    this.chipsEl.textContent = '';

    const add = (text, onRemove) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'vsh-chip';
      b.innerHTML =
        `<span></span><svg viewBox="0 0 12 12" aria-hidden="true" fill="none">` +
        `<path d="M1 1l10 10M11 1L1 11" stroke="currentColor"/></svg>`;
      b.querySelector('span').textContent = text;
      b.setAttribute('aria-label', `Retirer le filtre ${text}`);
      b.addEventListener('click', onRemove);
      this.chipsEl.appendChild(b);
    };

    this.boxes.forEach((box) => {
      if (!box.checked) return;
      add(this.labels.get(box) || box.value, () => {
        box.checked = false;
        this.apply();
      });
    });

    const clearPrice = (which) => () => {
      const el = this.priceInputs.find((i) => i.dataset.vshPrice === which);
      if (el) el.value = '';
      this.apply();
    };
    if (price.min !== null) add(`Min ${price.min} €`, clearPrice('min'));
    if (price.max !== null) add(`Max ${price.max} €`, clearPrice('max'));
  }

  clear() {
    this.boxes.forEach((b) => (b.checked = false));
    this.priceInputs.forEach((i) => (i.value = ''));
    if (this.sortEl) this.sortEl.value = 'manual';
    this.apply();
  }

  writeUrl(sel, price) {
    const u = new URL(window.location.href);
    ['house', 'type', 'material', 'color', 'condition', 'min', 'max', 'tri'].forEach((k) =>
      u.searchParams.delete(k)
    );
    Object.entries(sel).forEach(([k, v]) => u.searchParams.set(k, [...v].join(',')));
    if (price.min !== null) u.searchParams.set('min', price.min);
    if (price.max !== null) u.searchParams.set('max', price.max);
    if (this.sortEl && this.sortEl.value !== 'manual') u.searchParams.set('tri', this.sortEl.value);
    window.history.replaceState({}, '', u);
  }

  readUrl() {
    const q = new URLSearchParams(window.location.search);
    this.boxes.forEach((b) => {
      const raw = q.get(b.dataset.vshFacet);
      b.checked = raw ? raw.split(',').includes(b.value.toLowerCase()) : false;
    });
    this.priceInputs.forEach((i) => {
      const v = q.get(i.dataset.vshPrice === 'min' ? 'min' : 'max');
      i.value = v || '';
    });
    if (this.sortEl && q.get('tri')) this.sortEl.value = q.get('tri');

    const any = [...q.keys()].some((k) =>
      ['house', 'type', 'material', 'color', 'condition', 'min', 'max'].includes(k)
    );
    if (any && this.panel) {
      this.panel.dataset.open = 'true';
      this.querySelectorAll('[data-vsh-filter-toggle]').forEach((b) =>
        b.setAttribute('aria-expanded', 'true')
      );
    }
  }
}

if (!customElements.get('vsh-collection')) {
  customElements.define('vsh-collection', VshCollection);
}
